import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, Select, InputNumber, TimePicker, Button, message, Spin, Alert, Divider, Row, Col, Collapse, Tag } from 'antd';
import { BellOutlined, ClockCircleOutlined, SettingOutlined, SaveOutlined, CheckCircleOutlined, ExclamationCircleOutlined, BookOutlined, StarOutlined, TrophyOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import api from '../../services/api';

const { Option } = Select;
const { Panel } = Collapse;

const PushSettings = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [className, setClassName] = useState('');

  useEffect(() => {
    loadSettings();
    loadClassInfo();
  }, [classId]);

  const loadClassInfo = async () => {
    try {
      const res = await api.get(`/classes/${classId}`);
      if (res.data && res.data.success && res.data.data) {
        setClassName(res.data.data.name || '');
      } else if (res.data && res.data.name) {
        setClassName(res.data.name || '');
      }
    } catch (error) {
      console.error('获取班级信息失败:', error);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/parent-push/settings/${classId}`);
      if (res.data.success) {
        const data = res.data.data;
        form.setFieldsValue({
          push_enabled: Boolean(data.push_enabled),
          // 各类型开关（确保转换为布尔值）
          push_attendance: Boolean(data.push_attendance),
          push_homework: Boolean(data.push_homework),
          push_violation: Boolean(data.push_violation),
          push_points: Boolean(data.push_points),
          push_praise: Boolean(data.push_praise),
          // 考勤设置
          attendance_mode: data.attendance_mode || 'immediate',
          attendance_delay: data.attendance_delay || 60,
          attendance_time: data.attendance_time ? moment(data.attendance_time, 'HH:mm:ss') : moment('18:00:00', 'HH:mm:ss'),
          attendance_weekday: data.attendance_weekday || 5,
          // 作业设置
          homework_mode: data.homework_mode || 'delayed',
          homework_delay: data.homework_delay || 120,
          homework_time: data.homework_time ? moment(data.homework_time, 'HH:mm:ss') : moment('18:00:00', 'HH:mm:ss'),
          homework_weekday: data.homework_weekday || 5,
          // 违纪设置
          violation_mode: data.violation_mode || 'manual',
          violation_delay: data.violation_delay || 0,
          violation_severity: data.violation_severity || 'all',
          // 积分设置
          points_mode: data.points_mode || 'weekly',
          points_delay: data.points_delay || 0,
          points_time: data.points_time ? moment(data.points_time, 'HH:mm:ss') : moment('18:00:00', 'HH:mm:ss'),
          points_weekday: data.points_weekday || 5,
          exclude_positive_points: Boolean(data.exclude_positive_points),
          min_points_threshold: data.min_points_threshold || 0,
          // 表扬设置
          praise_mode: data.praise_mode || 'immediate',
          praise_delay: data.praise_delay || 0
        });
      }
    } catch (error) {
      message.error('加载设置失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      const data = {
        ...values,
        attendance_time: values.attendance_time ? values.attendance_time.format('HH:mm:ss') : '18:00:00',
        homework_time: values.homework_time ? values.homework_time.format('HH:mm:ss') : '18:00:00',
        points_time: values.points_time ? values.points_time.format('HH:mm:ss') : '18:00:00'
      };
      
      const res = await api.put(`/parent-push/settings/${classId}`, data);
      if (res.data.success) {
        message.success('保存成功');
      } else {
        message.error(res.data.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const pushModeOptions = [
    { value: 'immediate', label: '立即推送', desc: '事件发生后立即推送' },
    { value: 'delayed', label: '延迟推送', desc: '延迟一段时间后推送' },
    { value: 'daily', label: '每日汇总', desc: '每天定时推送汇总' },
    { value: 'weekly', label: '每周汇总', desc: '每周定时推送汇总' },
    { value: 'manual', label: '手动确认', desc: '教师手动确认后推送' }
  ];

  const weekdayOptions = [
    { value: 1, label: '周一' },
    { value: 2, label: '周二' },
    { value: 3, label: '周三' },
    { value: 4, label: '周四' },
    { value: 5, label: '周五' },
    { value: 6, label: '周六' },
    { value: 7, label: '周日' }
  ];

  const violationSeverityOptions = [
    { value: 'all', label: '全部违纪', desc: '所有违纪都推送' },
    { value: 'serious', label: '严重违纪', desc: '只推送严重违纪' },
    { value: 'custom', label: '自定义', desc: '根据违纪类型选择' }
  ];

  // Hooks 必须在条件渲染之前调用
  const pushEnabled = Form.useWatch('push_enabled', form);
  const pushAttendance = Form.useWatch('push_attendance', form);
  const pushHomework = Form.useWatch('push_homework', form);
  const pushViolation = Form.useWatch('push_violation', form);
  const pushPoints = Form.useWatch('push_points', form);
  const pushPraise = Form.useWatch('push_praise', form);
  const attendanceMode = Form.useWatch('attendance_mode', form);
  const homeworkMode = Form.useWatch('homework_mode', form);
  const violationMode = Form.useWatch('violation_mode', form);
  const pointsMode = Form.useWatch('points_mode', form);
  const praiseMode = Form.useWatch('praise_mode', form);

  // 渲染推送模式选择器
  const renderModeSelect = (name, options = pushModeOptions) => (
    <Form.Item name={name} label="推送模式" style={{ marginBottom: 8 }}>
      <Select style={{ width: '100%' }}>
        {options.map(opt => (
          <Option key={opt.value} value={opt.value}>
            <span style={{ fontWeight: 500 }}>{opt.label}</span>
            <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{opt.desc}</span>
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  // 渲染延迟设置
  const renderDelaySettings = (modeName, modeValue, delayName, timeName, weekdayName) => (
    <>
      {modeValue === 'delayed' && (
        <Form.Item name={delayName} label="延迟时间" style={{ marginBottom: 8 }}>
          <InputNumber min={5} max={1440} style={{ width: 150 }} addonAfter="分钟" />
        </Form.Item>
      )}
      {(modeValue === 'daily' || modeValue === 'weekly') && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name={timeName} label="推送时间" style={{ marginBottom: 8 }}>
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {modeValue === 'weekly' && (
            <Col span={12}>
              <Form.Item name={weekdayName} label="推送星期" style={{ marginBottom: 8 }}>
                <Select>
                  {weekdayOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      )}
      {modeValue === 'manual' && (
        <Alert 
          message="手动确认模式" 
          description="事件发生后，教师需要在微信小程序中确认后才会推送给家长" 
          type="info" 
          showIcon 
          style={{ marginBottom: 8 }}
        />
      )}
    </>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#999' }}>加载中...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      <Card 
        title={
          <span>
            <BellOutlined style={{ marginRight: 8 }} />
            家长推送设置 - {className}
          </span>
        }
        extra={
          <Button onClick={() => navigate(-1)}>返回</Button>
        }
      >
        <Alert
          message="推送说明"
          description={
            <div>
              <p>• 开启推送后，学生的考勤、作业、违纪等信息将自动推送给已绑定的家长</p>
              <p>• 每条通知只会推送给对应学生的家长，其他家长无法查看</p>
              <p>• 可以为每种通知类型设置不同的推送规则</p>
              <p>• 手动确认模式：事件发生后需要教师在小程序确认才推送</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            push_enabled: false,
            push_attendance: true,
            push_homework: true,
            push_violation: true,
            push_points: false,
            push_praise: true,
            attendance_mode: 'immediate',
            attendance_delay: 60,
            attendance_weekday: 5,
            homework_mode: 'delayed',
            homework_delay: 120,
            homework_weekday: 5,
            violation_mode: 'manual',
            violation_delay: 0,
            violation_severity: 'all',
            points_mode: 'weekly',
            points_delay: 0,
            points_weekday: 5,
            exclude_positive_points: true,
            min_points_threshold: 0,
            praise_mode: 'immediate',
            praise_delay: 0
          }}
        >
          {/* 总开关 */}
          <Form.Item
            name="push_enabled"
            label={<span style={{ fontSize: 16, fontWeight: 600 }}>启用家长推送</span>}
            valuePropName="checked"
          >
            <Switch 
              checkedChildren="开启" 
              unCheckedChildren="关闭"
              style={{ transform: 'scale(1.2)' }}
            />
          </Form.Item>

          {pushEnabled && (
            <>
              <Divider orientation="left">
                <SettingOutlined /> 推送类型设置
              </Divider>

              <Collapse defaultActiveKey={['attendance', 'homework', 'violation']} ghost>
                {/* 考勤设置 */}
                <Panel 
                  header={
                    <span>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      考勤通知
                      <Tag color={pushAttendance ? 'green' : 'default'} style={{ marginLeft: 8 }}>
                        {pushAttendance ? '已开启' : '已关闭'}
                      </Tag>
                    </span>
                  } 
                  key="attendance"
                >
                  <Form.Item name="push_attendance" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    <span style={{ marginLeft: 12, color: '#666' }}>迟到、缺勤、请假通知</span>
                  </Form.Item>
                  {pushAttendance && (
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                      {renderModeSelect('attendance_mode')}
                      {renderDelaySettings('attendance_mode', attendanceMode, 'attendance_delay', 'attendance_time', 'attendance_weekday')}
                    </div>
                  )}
                </Panel>

                {/* 作业设置 */}
                <Panel 
                  header={
                    <span>
                      <BookOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                      作业通知
                      <Tag color={pushHomework ? 'blue' : 'default'} style={{ marginLeft: 8 }}>
                        {pushHomework ? '已开启' : '已关闭'}
                      </Tag>
                    </span>
                  } 
                  key="homework"
                >
                  <Form.Item name="push_homework" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    <span style={{ marginLeft: 12, color: '#666' }}>作业未交提醒</span>
                  </Form.Item>
                  {pushHomework && (
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                      {renderModeSelect('homework_mode')}
                      {renderDelaySettings('homework_mode', homeworkMode, 'homework_delay', 'homework_time', 'homework_weekday')}
                      {homeworkMode === 'delayed' && (
                        <Alert 
                          message="建议：设置2小时延迟，给学生补交作业的时间" 
                          type="warning" 
                          showIcon 
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </div>
                  )}
                </Panel>

                {/* 违纪设置 */}
                <Panel 
                  header={
                    <span>
                      <ExclamationCircleOutlined style={{ color: '#f5222d', marginRight: 8 }} />
                      违纪通知
                      <Tag color={pushViolation ? 'red' : 'default'} style={{ marginLeft: 8 }}>
                        {pushViolation ? '已开启' : '已关闭'}
                      </Tag>
                    </span>
                  } 
                  key="violation"
                >
                  <Form.Item name="push_violation" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    <span style={{ marginLeft: 12, color: '#666' }}>违纪记录通知</span>
                  </Form.Item>
                  {pushViolation && (
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                      {renderModeSelect('violation_mode')}
                      {renderDelaySettings('violation_mode', violationMode, 'violation_delay', null, null)}
                      <Form.Item name="violation_severity" label="推送范围" style={{ marginBottom: 8 }}>
                        <Select>
                          {violationSeverityOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>
                              <span style={{ fontWeight: 500 }}>{opt.label}</span>
                              <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{opt.desc}</span>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  )}
                </Panel>

                {/* 积分设置 */}
                <Panel 
                  header={
                    <span>
                      <StarOutlined style={{ color: '#faad14', marginRight: 8 }} />
                      积分变动
                      <Tag color={pushPoints ? 'gold' : 'default'} style={{ marginLeft: 8 }}>
                        {pushPoints ? '已开启' : '已关闭'}
                      </Tag>
                    </span>
                  } 
                  key="points"
                >
                  <Form.Item name="push_points" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    <span style={{ marginLeft: 12, color: '#666' }}>加分/扣分通知</span>
                  </Form.Item>
                  {pushPoints && (
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                      {renderModeSelect('points_mode')}
                      {renderDelaySettings('points_mode', pointsMode, 'points_delay', 'points_time', 'points_weekday')}
                      <Row gutter={16} style={{ marginTop: 8 }}>
                        <Col span={12}>
                          <Form.Item name="exclude_positive_points" label="只推送扣分" valuePropName="checked">
                            <Switch checkedChildren="是" unCheckedChildren="否" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="min_points_threshold" label="最小推送分值">
                            <InputNumber min={0} max={100} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Panel>

                {/* 表扬设置 */}
                <Panel 
                  header={
                    <span>
                      <TrophyOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                      表扬通知
                      <Tag color={pushPraise ? 'purple' : 'default'} style={{ marginLeft: 8 }}>
                        {pushPraise ? '已开启' : '已关闭'}
                      </Tag>
                    </span>
                  } 
                  key="praise"
                >
                  <Form.Item name="push_praise" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    <span style={{ marginLeft: 12, color: '#666' }}>学生受表扬通知</span>
                  </Form.Item>
                  {pushPraise && (
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
                      {renderModeSelect('praise_mode')}
                      {renderDelaySettings('praise_mode', praiseMode, 'praise_delay', null, null)}
                    </div>
                  )}
                </Panel>
              </Collapse>
            </>
          )}

          <Divider />

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={saving}
              icon={<SaveOutlined />}
              size="large"
              style={{ width: '100%' }}
            >
              保存设置
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PushSettings;
